// The customer's Billing tab (was the commercial-terms screen, SCR-009): invoices by month on the
// left; on the right the rate plan in force with its assignments, who pays whom (billing mode and
// its schedule), trials and credits, and cost visibility. Plan and trial controls apply only to a
// billed month; a not-billed customer reads "Not billed — proxy only" in their place.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { LabelText } from "@nexus/ui";
import { actorTitle, knownActor } from "../../ui/identity";
import { currentMonth } from "@nexus/ui";
import { Card, Segmented } from "@nexus/ui";
import { Link } from "../../ui/shell";
import { ErrorBanner, Loading } from "@nexus/ui";
import { putCostVisibilityRequest } from "../orgs/model";
import { WhoPaysWhom } from "./billing-mode";
import { InvoicesCard } from "./invoices-card";
import {
  COST_VISIBILITY_LABELS,
  COST_VISIBILITY_OPTIONS,
  NOT_BILLED_MESSAGE,
  activePlans,
  assignmentRow,
  compareAssignmentMonth,
  isBilled,
  loadAssignments,
  loadEffectivePlan,
  loadOrgBillingSettings,
  loadPlans,
  loadTrials,
  planSince,
  planSummary,
  shortMonth,
  type CostVisibility,
  type EffectiveRatePlan,
  type OrgBillingSettings,
  type PlanAssignment,
  type RatePlan,
  type Trial,
  type Viewer,
} from "./model";
import { TrialsCard } from "./trials-card";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

function RatePlanCard({
  billed,
  effective,
  plans,
  assignments,
  viewer,
}: {
  billed: boolean;
  effective: EffectiveRatePlan | null;
  plans: readonly RatePlan[];
  assignments: readonly PlanAssignment[];
  viewer: Viewer;
}) {
  const names = new Map(plans.map((plan) => [plan.id, plan.name]));
  const plan = effective ? plans.find((p) => p.id === effective.planId) : undefined;
  const since = effective ? planSince(assignments, effective.planId, effective.period) : null;
  const rows = [...assignments].map((a) => ({ a, row: assignmentRow(a, names.get(a.planId) ?? null, viewer) })).sort((x, y) => compareAssignmentMonth(x.row, y.row));

  return (
    <Card title="Rate plan" help="terms.ratePlan">
      {!billed ? (
        <p class="card-note">{NOT_BILLED_MESSAGE} Switch this customer to billed under Who pays whom before assigning a plan.</p>
      ) : (
        <>
          {effective ? (
            <div class="stack-sm">
              <Link href={`/rate-plans/${encodeURIComponent(effective.planId)}`} class="lead-link">
                {plan?.name ?? names.get(effective.planId) ?? effective.planId}
              </Link>
              <p class="card-note">
                {plan ? `${planSummary(plan)} ` : ""}
                {effective.source === "default" ? "The fleet default plan (no plan of the customer's own this month)." : since ? `Since ${shortMonth(since)}.` : ""}
              </p>
            </div>
          ) : (
            <p class="card-note">No plan prices this month yet.</p>
          )}
          {rows.length > 0 && (
            <div class="table-scroll">
              <table class="data-table data-table-compact">
                <caption>Assignments</caption>
                <thead>
                  <tr>
                    <th scope="col">From</th>
                    <th scope="col">Plan</th>
                    <th scope="col">Assigned by</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ a, row }) => (
                    <tr key={row.id}>
                      <td>{shortMonth(row.effectiveMonth)}</td>
                      <td>
                        <Link href={`/rate-plans/${encodeURIComponent(row.planId)}`}>{row.planName}</Link>
                      </td>
                      <td title={actorTitle(a.createdBy, knownActor(a, "createdBy"))}>{row.createdBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function CostVisibilityCard({
  orgId,
  value,
  canWrite,
  onChange,
}: {
  orgId: string;
  value: CostVisibility;
  canWrite: boolean;
  onChange(value: CostVisibility): void;
}) {
  const client = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const choose = (next: CostVisibility) => {
    if (!canWrite || busy || next === value) return;
    setBusy(true);
    setError(null);
    client
      .put("/v1/admin/orgs/{orgId}/billing-settings/cost-visibility", { path: { orgId }, body: putCostVisibilityRequest(next) })
      .then((updated) => onChange(updated.costVisibility))
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setBusy(false));
  };
  return (
    <Card title="Cost visibility" help="terms.costVisibility">
      <p class="card-note">Whether the customer's own admins and service keys see AWS cost. Takes effect at once, for every month.</p>
      {canWrite ? (
        <div class="field">
          <LabelText id="cost-visibility-label" label="The customer sees" help="terms.costVisibility" />
          <Segmented
            labelledBy="cost-visibility-label"
            small
            options={COST_VISIBILITY_OPTIONS.map((option) => ({ value: option, label: COST_VISIBILITY_LABELS[option] }))}
            value={value}
            onChange={choose}
          />
        </div>
      ) : (
        <p>{COST_VISIBILITY_LABELS[value]}</p>
      )}
      {error && <ErrorBanner error={error} />}
    </Card>
  );
}

export function BillingTab({ orgId, customer }: { orgId: string; customer: string }) {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canWrite = session?.can("billing:write") ?? false;
  const viewer = session?.me ?? null;

  const [settings, setSettings] = useState<OrgBillingSettings | null>(null);
  const [assignments, setAssignments] = useState<readonly PlanAssignment[] | null>(null);
  const [plans, setPlans] = useState<readonly RatePlan[]>([]);
  const [effective, setEffective] = useState<EffectiveRatePlan | null>(null);
  const [trials, setTrials] = useState<readonly Trial[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const reloadPlan = () => {
    void loadAssignments(client, orgId).then((r) => ("items" in r ? setAssignments(r.items) : setError(r.error)));
    void loadEffectivePlan(client, orgId, currentMonth(new Date())).then(setEffective);
  };

  const reload = () => {
    setError(null);
    void loadOrgBillingSettings(client, orgId).then((state) => (state.kind === "loaded" ? setSettings(state.settings) : setError(state.error)));
    void loadPlans(client).then(setPlans);
    void loadTrials(client, orgId).then((r) => ("items" in r ? setTrials(r.items) : setError(r.error)));
    reloadPlan();
  };

  useEffect(reload, [client, orgId]);

  const right = () => {
    if (error) return <ErrorBanner error={error} onRetry={reload} />;
    if (!settings || !assignments || !trials) return <Loading label="Loading billing terms…" />;
    const billed = isBilled(settings);
    return (
      <>
        <RatePlanCard billed={billed} effective={effective} plans={plans} assignments={assignments} viewer={viewer} />
        <WhoPaysWhom
          orgId={orgId}
          customer={customer}
          settings={settings}
          canWrite={canWrite}
          plans={activePlans(plans)}
          viewer={viewer}
          onSaved={(next) => {
            setSettings(next);
            reloadPlan();
          }}
        />
        <TrialsCard orgId={orgId} billed={billed} canWrite={canWrite} trials={trials} onTrials={setTrials} />
        <CostVisibilityCard
          orgId={orgId}
          value={settings.costVisibility}
          canWrite={canWrite}
          onChange={(costVisibility) => setSettings({ ...settings, costVisibility })}
        />
      </>
    );
  };

  return (
    <div class="split billing-tab">
      <InvoicesCard orgId={orgId} canWrite={canWrite} />
      <div class="stack">{right()}</div>
    </div>
  );
}
